import React, { useState, useEffect } from 'react';
import { useAudit } from '../context/AuditContext';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { NavigationControls } from '../components/NavigationControls';
import { TooltipWrapper, TOOLTIP_CONTENT } from '../components/TooltipWrapper';
import { OpportunityMatrix } from '../components/OpportunityMatrix';
import { Target, Lightbulb, TrendingUp } from 'lucide-react';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

export default function MatrixPage() {
  const { auditData, updateMatrixItemRating, getOverallScore, getRangeLabel } = useAudit();
  
  // Initialize matrix items from ROI items on mount
  useEffect(() => {
    // This will happen automatically through context
  }, []);
  
  const getQuadrant = (impact, effort) => {
    // Using 3 as the midpoint threshold for 1-5 scale
    if (impact > 3 && effort <= 3) return { label: 'Quick Wins', color: 'bg-accent text-accent-foreground' };
    if (impact > 3 && effort > 3) return { label: 'Strategic Plays', color: 'bg-primary text-white' };
    if (impact <= 3 && effort <= 3) return { label: 'Fill-Ins', color: 'bg-muted text-foreground' };
    return { label: 'Time Sinks', color: 'bg-gray-400 text-gray-700' };
  };

  const overallScore = getOverallScore();
  const rangeLabel = getRangeLabel(overallScore);
  
  // Create matrix items from ROI items with ratings
  const matrixItems = auditData.roi_items.map((roiItem, index) => {
    const existing = auditData.matrix_items.find(m => m.roi_index === index);
    return {
      roi_index: index,
      name: roiItem.process,
      category: roiItem.category,
      annual: roiItem.annual,
      difficulty: roiItem.difficulty || 3, // Use pre-assigned difficulty from ROI item
      impact: existing?.impact || 0,
      effort: roiItem.difficulty || 3 // For backward compatibility with visual matrix
    };
  });

  return (
    <PageFrame pageNumber={8} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12 bg-gray-50/50">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header bar */}
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <div className="flex items-center justify-center gap-2">
              <h1 className="text-3xl font-extrabold uppercase tracking-wide" data-testid="section-title">
                Score Summary & Opportunity Matrix
              </h1>
              <TooltipWrapper content={TOOLTIP_CONTENT.impactEffortMatrix} iconSize={20} className="text-white" />
            </div>
            <p className="mt-2 text-white/90">Where AI Can Deliver ROI First</p>
          </div>

          {/* Score Summary */}
          <SectionCard title="Your Overall Score" className="mt-6">
            <div className="grid md:grid-cols-5 gap-4 mb-6">
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground mb-1">Operations</div>
                <div className="text-2xl font-bold text-primary" data-testid="ops-score">
                  {auditData.sections.operations.total}
                </div>
              </div>
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground mb-1">Customer</div>
                <div className="text-2xl font-bold text-primary" data-testid="customer-score">
                  {auditData.sections.customer.total}
                </div>
              </div>
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground mb-1">Marketing</div>
                <div className="text-2xl font-bold text-primary" data-testid="marketing-score">
                  {auditData.sections.marketing.total}
                </div>
              </div>
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground mb-1">Team</div>
                <div className="text-2xl font-bold text-primary" data-testid="team-score">
                  {auditData.sections.team.total}
                </div>
              </div>
              <div className="bg-accent rounded-lg p-4 text-center">
                <div className="text-sm text-accent-foreground mb-1 font-semibold">Overall</div>
                <div className="text-3xl font-bold text-secondary" data-testid="overall-score">
                  {overallScore}
                </div>
              </div>
            </div>

            {/* Range Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Score Range</TableHead>
                  <TableHead>Assessment</TableHead>
                  <TableHead>Your Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">0-40</TableCell>
                  <TableCell>Efficient - Running smoothly</TableCell>
                  <TableCell>
                    {rangeLabel === 'Efficient' && <Badge className="bg-green-500">Current</Badge>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">41-80</TableCell>
                  <TableCell>Pilot AI - Moderate opportunity</TableCell>
                  <TableCell>
                    {rangeLabel === 'Pilot AI' && <Badge className="bg-primary">Current</Badge>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">81-100</TableCell>
                  <TableCell>High ROI Potential - Significant gains possible</TableCell>
                  <TableCell>
                    {rangeLabel === 'High ROI Potential' && <Badge className="bg-accent">Current</Badge>}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </SectionCard>

          {/* Insight */}
          <div className="bg-primary text-white rounded-lg p-6 mt-6">
            <div className="flex items-start gap-4">
              <Lightbulb className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-bold text-lg mb-2">What This Means</h3>
                <p>
                  Use the matrix below to prioritize your AI opportunities. Rate each task's potential impact on your business and implementation difficulty to identify your best starting points.
                </p>
              </div>
            </div>
          </div>

          {/* Map Your Opportunities - Auto-populated from ROI items */}
          <SectionCard title="Rate Your Opportunities" icon={Target} className="mt-6">
            {matrixItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No opportunities to rate yet.</p>
                <p className="text-sm mt-2">Go back to the ROI Estimator page to add tasks and work responsibilities first.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  For each opportunity from your ROI analysis, rate the potential business impact and implementation difficulty.
                </p>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[35%]">Opportunity</TableHead>
                      <TableHead className="w-[15%] text-center">
                        <div className="flex items-center justify-center gap-1">
                          Impact (1-5)
                          <TooltipWrapper content={TOOLTIP_CONTENT.impact} iconSize={14} />
                        </div>
                      </TableHead>
                      <TableHead className="w-[20%] text-center">
                        <div className="flex items-center justify-center gap-1">
                          Difficulty
                          <TooltipWrapper content={TOOLTIP_CONTENT.difficulty} iconSize={14} />
                        </div>
                      </TableHead>
                      <TableHead className="w-[15%]">Quadrant</TableHead>
                      <TableHead className="w-[15%] text-right">Annual Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matrixItems.map((item, index) => {
                      const difficulty = item.effort || item.difficulty || 3; // Use effort (for existing items) or difficulty (new items)
                      const quadrant = item.impact > 0 && difficulty > 0 ? getQuadrant(item.impact, difficulty) : null;
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            <div className="whitespace-nowrap">
                              <div>{item.name}</div>
                              <div className="text-xs text-muted-foreground capitalize">{item.category}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Select 
                              value={item.impact > 0 ? item.impact.toString() : ""} 
                              onValueChange={(val) => updateMatrixItemRating(item.roi_index, 'impact', parseInt(val))}
                            >
                              <SelectTrigger className="w-20 mx-auto" data-testid={`matrix-impact-${index}`}>
                                <SelectValue placeholder="-" />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 2, 3, 4, 5].map(n => (
                                  <SelectItem key={n} value={n.toString()}>
                                    {n}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                              <span className="text-sm font-medium">{difficulty}</span>
                              <span className="text-xs text-muted-foreground">
                                ({difficulty === 1 ? 'Very Easy' : difficulty === 2 ? 'Easy' : difficulty === 3 ? 'Moderate' : difficulty === 4 ? 'Difficult' : 'Very Difficult'})
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {quadrant ? (
                              <Badge className={`${quadrant.color} whitespace-nowrap`}>{quadrant.label}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground whitespace-nowrap">Rate impact</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-primary">
                            ${item.annual.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Legend */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-accent text-accent-foreground">Quick Wins</Badge>
                    <span className="text-xs text-muted-foreground">High Impact, Low Difficulty</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary text-white">Strategic Plays</Badge>
                    <span className="text-xs text-muted-foreground">High Impact, High Difficulty</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-muted text-foreground">Fill-Ins</Badge>
                    <span className="text-xs text-muted-foreground">Low Impact, Low Difficulty</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-gray-300 text-gray-700">Time Sinks</Badge>
                    <span className="text-xs text-muted-foreground">Low Impact, High Difficulty</span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Visual Opportunity Matrix */}
          {matrixItems.filter(m => m.impact > 0 && m.effort > 0).length > 0 && (
            <SectionCard title="Strategic Prioritization Map" icon={TrendingUp} className="mt-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  This visual map shows where each opportunity falls based on your ratings. Focus on the <strong>Quick Wins</strong> quadrant for immediate value.
                </p>
                <OpportunityMatrix opportunities={matrixItems} />
              </div>
            </SectionCard>
          )}

          {/* Navigation */}
          <div className="pt-4">
            <NavigationControls currentPage={8} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
}